import React, { useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, Menu, X } from "lucide-react";
import { ArgomentiSidebar } from "../components/ArgomentiSidebar";
import { TopicHero, FeedTabs } from "../components/TopicFeedChrome";
import { VoicePost } from "../components/VoicePost";
import { InFeedSponsored } from "../components/InFeedSponsored";
import { ComposerBar } from "../components/ComposerBar";
import { DailyNewsBanner } from "../components/DailyNewsBanner";
import { ShareDialog } from "../components/ShareDialog";
import { IdentityRecoveryDialog } from "../components/IdentityRecoveryDialog";
import { useFeedPosts } from "../hooks/useFeedPosts";
import { diffusePostWithRepair, diffusionErrorMessage, requiresIdentityRecovery } from "../lib/diffuseApi";
import "./ArgumentRoute.diffuse.css";

function chunkBy(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

function DiffuseNotice({ notice, onClose }) {
  if (!notice || typeof document === "undefined") return null;
  const isError = notice.kind === "error";
  const isWorking = notice.kind === "working";
  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-[2147483000] flex justify-center px-3 sm:bottom-6"
      role={isError ? "alert" : "status"}
      data-testid="diffuse-notice"
    >
      <div className="pointer-events-auto w-[min(92vw,560px)] rounded-2xl border-2 border-ink bg-cream p-3 shadow-[5px_5px_0_#171717]">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${isError ? "bg-primary" : isWorking ? "bg-amber-500 animate-pulse" : "bg-emerald-600"}`} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <strong className="block text-sm text-ink">
              {isError ? "Diffusione non completata" : isWorking ? "Diffusione in corso" : "Messaggio diffuso"}
            </strong>
            <p className={`mt-1 text-xs leading-5 ${isError ? "text-primary" : "text-muted"}`}>
              {notice.message}
            </p>
          </div>
          {!isWorking && (
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-white text-primary"
              aria-label="Chiudi avviso Diffondi"
            >
              <X size={17} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ArgumentFeed({ topic, activeTab, onChangeTab, feedAds, onSwapFeedAd, posts, onDeleted, onOpenMessages }) {
  const chunks = chunkBy(posts, 3);
  const [sharePost, setSharePost] = useState(null);
  const [diffusingPostId, setDiffusingPostId] = useState(null);
  const [diffuseNotice, setDiffuseNotice] = useState(null);
  const [recoveryPostId, setRecoveryPostId] = useState(null);

  const startDiffuse = async (postId, actionElement) => {
    if (!postId || diffusingPostId) return false;
    actionElement?.setAttribute?.("aria-busy", "true");
    actionElement?.blur?.();
    setDiffusingPostId(postId);
    setDiffuseNotice({ kind: "working", message: "Verifica dell’identità e pubblicazione nella rete interna…" });
    try {
      await diffusePostWithRepair(postId);
      setDiffuseNotice({ kind: "success", message: "Il messaggio è stato pubblicato nella rete delle persone che ti seguono." });
      window.setTimeout(() => setDiffuseNotice(null), 3200);
      return true;
    } catch (error) {
      if (requiresIdentityRecovery(error)) {
        setDiffuseNotice(null);
        setRecoveryPostId(postId);
        return false;
      }
      setDiffuseNotice({ kind: "error", message: diffusionErrorMessage(error) });
      return false;
    } finally {
      setDiffusingPostId(null);
      actionElement?.removeAttribute?.("aria-busy");
      actionElement?.blur?.();
    }
  };

  const handleFeedAction = (event) => {
    const action = event.target?.closest?.('[data-testid^="share-"], [data-testid^="dm-"], [data-testid^="diffuse-"]');
    if (!action) return;
    const testId = action.getAttribute("data-testid") || "";

    if (testId.startsWith("share-")) {
      const postId = testId.slice("share-".length);
      const targetPost = posts.find((item) => String(item.id) === postId);
      if (!targetPost) return;
      event.preventDefault();
      event.stopPropagation();
      action.blur?.();
      setSharePost(targetPost);
      return;
    }

    if (testId.startsWith("diffuse-")) {
      const postId = testId.slice("diffuse-".length);
      const targetPost = posts.find((item) => String(item.id) === postId);
      if (!targetPost) return;
      event.preventDefault();
      event.stopPropagation();
      action.blur?.();
      void startDiffuse(postId, action);
      return;
    }

    if (!testId.startsWith("dm-") || typeof onOpenMessages !== "function") return;
    const postId = testId.slice("dm-".length);
    const targetPost = posts.find((item) => String(item.id) === postId);
    if (!targetPost?.identityId) return;
    event.preventDefault();
    event.stopPropagation();
    action.blur?.();
    onOpenMessages(targetPost.identityId);
  };

  const retryAfterRecovery = async () => {
    const postId = recoveryPostId;
    if (!postId) return;
    await startDiffuse(postId, null);
  };

  return (
    <div className="max-w-[1100px] mx-auto px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="card overflow-visible sm:overflow-hidden" data-testid="topic-feed">
        <div className="pointer-events-none sticky top-0 z-10 overflow-hidden rounded-t-3xl sm:static sm:rounded-none" data-testid="topic-hero-sticky">
          <TopicHero topic={topic} />
        </div>
        <FeedTabs activeTab={activeTab} onChange={onChangeTab} />
        <div className="px-3 sm:px-6" onClickCapture={handleFeedAction}>
          {/* Admin-only "Notizia del giorno" banner. Renders null for
              regular users when the moderator has NOT published anything,
              keeping the feed exactly as before. */}
          <DailyNewsBanner topicSlug={topic?.slug} />
          {posts.length === 0 && (
            <div className="py-16 text-center text-muted" data-testid={activeTab === "seguiti" ? "feed-empty-followed" : "feed-empty"}>
              {activeTab === "seguiti" ? (
                <>
                  <p className="text-sm">Non segui ancora nessuno in questo argomento.</p>
                  <p className="text-xs mt-2">Clicca "Segui" su un post reale nella tab "Recenti" per iniziare.</p>
                </>
              ) : (
                <>
                  <p className="text-sm">Non ci sono ancora messaggi in questo argomento.</p>
                  <p className="text-xs mt-2">Puoi essere il primo a pubblicarne uno.</p>
                </>
              )}
            </div>
          )}
          {chunks.map((chunk, idx) => (
            <React.Fragment key={`chunk-${idx}`}>
              {chunk.map((p) => (
                <VoicePost key={p.id} post={p} topicSlug={topic?.slug} onDeleted={onDeleted} onOpenMessages={onOpenMessages} />
              ))}
              <div className="pb-1 pt-3" data-testid={`in-feed-slot-${idx}`}>
                <InFeedSponsored
                  campaign={feedAds[idx % Math.max(feedAds.length, 1)]}
                  onSwap={() => onSwapFeedAd(idx)}
                />
              </div>
            </React.Fragment>
          ))}
          <div className="h-6" />
        </div>
      </div>
      <ShareDialog post={sharePost} topicSlug={topic?.slug} onClose={() => setSharePost(null)} />
      <DiffuseNotice notice={diffuseNotice} onClose={() => setDiffuseNotice(null)} />
      <IdentityRecoveryDialog
        open={Boolean(recoveryPostId)}
        onClose={() => setRecoveryPostId(null)}
        onRecovered={retryAfterRecovery}
      />
    </div>
  );
}

export function ArgumentRoute({
  topics,
  activeTopic,
  onSelectTopic,
  onBackHome,
  heroAd,
  onSwapHero,
  feedAds,
  onSwapFeedAd,
  activeTab,
  onChangeTab,
  onOpenMessages,
  onOpenLegal,
  onOpenAdmin,
  onGoHome,
}) {
  const [mobileTopicsOpen, setMobileTopicsOpen] = useState(false);
  const currentTopic = topics.find((t) => t.slug === activeTopic);
  const feed = useFeedPosts(activeTopic, activeTab === "seguiti");
  const selectTopic = (slug) => {
    onSelectTopic(slug);
    setMobileTopicsOpen(false);
  };
  const sidebar = (
    <ArgomentiSidebar
      topics={topics}
      activeTopic={activeTopic}
      onSelectTopic={selectTopic}
      heroAd={heroAd}
      onSwapHero={onSwapHero}
      onBackHome={onBackHome}
    />
  );
  return (
    <div className="flex-1 min-h-0 flex relative" data-testid="argument-layout">
      <aside className="hidden lg:block w-[25%] max-w-[380px] min-w-[280px] shrink-0 border-r border-edge">
        {sidebar}
      </aside>
      {mobileTopicsOpen && (
        <div className="fixed inset-0 z-[1000] lg:hidden" data-testid="mobile-topics-drawer">
          <button type="button" className="absolute inset-0 z-0 bg-black/45 touch-manipulation" onClick={() => setMobileTopicsOpen(false)} aria-label="Chiudi elenco argomenti" />
          <aside className="absolute inset-y-0 left-0 z-10 w-[88vw] max-w-[360px] bg-cream shadow-2xl pointer-events-auto">
            <button type="button" onClick={() => setMobileTopicsOpen(false)} className="absolute top-2 right-2 z-20 w-10 h-10 rounded-full bg-white border border-edge flex items-center justify-center touch-manipulation" aria-label="Chiudi">
              <X size={20} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-cream" data-testid="right-pane">
        <div className="relative z-40 pointer-events-auto lg:hidden shrink-0 border-b border-edge bg-cream px-2 py-2 flex items-center gap-2">
          <button type="button" onClick={() => setMobileTopicsOpen(true)} className="relative z-10 h-10 px-3 rounded-full bg-white border border-edge inline-flex items-center gap-2 text-sm font-semibold touch-manipulation" data-testid="mobile-open-topics">
            <Menu size={18} /> Argomenti
          </button>
          <div className="flex-1" aria-hidden="true" />
          <button type="button" onClick={onBackHome} className="relative z-10 h-10 px-3 rounded-full text-sm text-muted inline-flex items-center gap-1 touch-manipulation" aria-label="Torna alla home">
            <ChevronLeft size={16} /> Home
          </button>
        </div>
        <div className="relative z-0 flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="feed-scroll">
          <ArgumentFeed
            topic={currentTopic}
            activeTab={activeTab}
            onChangeTab={onChangeTab}
            feedAds={feedAds}
            onSwapFeedAd={onSwapFeedAd}
            posts={feed.posts}
            onDeleted={feed.refresh}
            onOpenMessages={onOpenMessages}
          />
        </div>
        <div className="shrink-0 border-t border-edge bg-cream/95 backdrop-blur">
          <div className="max-w-[1100px] mx-auto">
            <ComposerBar topicSlug={activeTopic} onPublished={feed.refresh} />
          </div>
        </div>
      </div>
    </div>
  );
}
